import { useCallback, useEffect, useRef, useState } from 'react';
import config from '../../config.js';
import {
  CAMERA_BASE_URL,
  buildCameraList,
  createBlobCache,
  datesUrl,
  fetchRelayBlobUrl,
  fetchRelayJson,
  fileUrl,
  listUrl,
  thumbUrl,
  todayCentral,
} from '../../utils/cameraRelay';
import { activityByStation, mergeJobRuns, timelineBlocks } from '../../utils/stationActivity';

const API_BASE = config.API_BASE_URL;

export function usePageVisible() {
  const [visible, setVisible] = useState(typeof document === 'undefined' ? true : !document.hidden);
  useEffect(() => {
    const onVis = () => setVisible(!document.hidden);
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);
  return visible;
}

/** Runs `fn` now and every `ms` while `active`; pauses while the tab is hidden. */
export function usePolling(fn, ms, active) {
  const visible = usePageVisible();
  const fnRef = useRef(fn);
  fnRef.current = fn;
  useEffect(() => {
    if (!active || !visible) return undefined;
    fnRef.current();
    const id = setInterval(() => fnRef.current(), ms);
    return () => clearInterval(id);
  }, [ms, active, visible]);
}

/** Relay camera list + health, refreshed every minute. */
export function useRelay() {
  const [cameras, setCameras] = useState(null); // null = loading
  const [health, setHealth] = useState(null);
  const [healthError, setHealthError] = useState(false);
  const configKeyRef = useRef('');

  const loadConfig = useCallback(async () => {
    try {
      const data = await fetchRelayJson(`${CAMERA_BASE_URL}/cameras/config`);
      const key = JSON.stringify(data);
      // Keep the same array when nothing changed so tiles don't re-mount.
      if (key !== configKeyRef.current) {
        configKeyRef.current = key;
        setCameras(buildCameraList(data));
      }
    } catch (e) {
      setCameras((prev) => prev || []);
    }
  }, []);

  const loadHealth = useCallback(async () => {
    try {
      setHealth(await fetchRelayJson(`${CAMERA_BASE_URL}/health`));
      setHealthError(false);
    } catch (e) {
      setHealthError(true);
    }
  }, []);

  usePolling(loadConfig, 60000, true);
  usePolling(loadHealth, 60000, true);

  return { cameras, health, healthError, reloadHealth: loadHealth };
}

/** How many days of recordings are on disk (from the first camera's date list). */
export function useRetention(camera) {
  const [info, setInfo] = useState(null);
  const station = camera?.station;
  const index = camera?.index;
  useEffect(() => {
    if (station == null) return undefined;
    let cancelled = false;
    fetchRelayJson(datesUrl(station, index))
      .then((d) => {
        if (cancelled) return;
        const dates = Array.isArray(d?.dates) ? d.dates.slice().sort() : [];
        setInfo({ days: dates.length, oldest: dates[0] || null, dates });
      })
      .catch(() => { if (!cancelled) setInfo(null); });
    return () => { cancelled = true; };
  }, [station, index]);
  return info;
}

/** Live JobQ station activity ({ [station]: describeStation }), every minute while `active`. */
export function useStationActivity(active) {
  const [activity, setActivity] = useState({});
  const [error, setError] = useState(false);
  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/stations/activity`, { cache: 'no-store' });
      if (!res.ok) throw new Error(`activity ${res.status}`);
      setActivity(activityByStation(await res.json()));
      setError(false);
    } catch (e) {
      setError(true);
    }
  }, []);
  usePolling(load, 60000, active);
  return { activity, error };
}

// ── One camera's day, for the phone's Playback list (mobile/screens/CamerasScreen.jsx) ──

const relayStatus = (e) => {
  const m = /relay (\d+)/.exec((e && e.message) || '');
  return m ? ` (${m[1]})` : '';
};

/**
 * A camera's clips on a Central day, as the relay lists them (newest first), while `active`. Until
 * this camera + day's list is in, `files` is empty: right after a change the previous camera's
 * list must not be read as this one's (the clip names are the same on every camera).
 */
export function useClipList(station, index, date, active) {
  const key = active && station ? `${station}|${index}|${date}` : '';
  const [state, setState] = useState({ key: '', files: [], error: '' });
  useEffect(() => {
    if (!key) return undefined;
    let cancelled = false;
    fetchRelayJson(listUrl(station, index, date))
      .then((data) => {
        if (!cancelled) setState({ key, files: Array.isArray(data?.files) ? data.files : [], error: '' });
      })
      .catch((e) => {
        if (!cancelled) setState({ key, files: [], error: `Failed to load recordings${relayStatus(e)}` });
      });
    return () => { cancelled = true; };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const current = Boolean(key) && state.key === key;
  return {
    files: current ? state.files : EMPTY,
    loading: Boolean(key) && !current,
    error: current ? state.error : '',
  };
}

const EMPTY = Object.freeze([]);

const NO_VIDEO = { url: '', loading: false, error: '' };

/** One clip as a blob: URL (CF Access can't do media range requests), revoked when it changes. A clip
 *  left before it arrived is cancelled (a phone on cellular shouldn't keep downloading it). */
export function useClipVideo(station, index, file, active) {
  const [state, setState] = useState(NO_VIDEO);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!active || !station || !file) {
      setState(NO_VIDEO);
      return undefined;
    }
    let cancelled = false;
    let objUrl = '';
    const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    setState({ url: '', loading: true, error: '' });
    fetchRelayBlobUrl(fileUrl(station, index, file), { signal: ctrl ? ctrl.signal : undefined })
      .then((u) => {
        objUrl = u;
        if (cancelled) {
          try { URL.revokeObjectURL(u); } catch (e) { /* noop */ }
        } else {
          setState({ url: u, loading: false, error: '' });
        }
      })
      .catch((e) => {
        if (!cancelled) setState({ url: '', loading: false, error: `Failed to load video${relayStatus(e)}` });
      });
    return () => {
      cancelled = true;
      if (ctrl) ctrl.abort();
      if (objUrl) {
        try { URL.revokeObjectURL(objUrl); } catch (e) { /* noop */ }
      }
    };
  }, [station, index, file, active, attempt]);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { ...state, retry };
}

/**
 * Clip thumbnails loaded as their rows scroll near the screen: `observe(el)` on a row carrying
 * data-filename, `thumbFor(file)` = its blob: URL once loaded. At most `max` kept: the oldest loaded
 * goes first (reading them never counts as a use, so a render can't reorder the cache).
 */
export function useClipThumbs(station, index, date, active, max = 100) {
  const cache = useRef(null);
  if (!cache.current) cache.current = createBlobCache(max);
  const io = useRef(null);
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!active || !station) return undefined;
    const thumbs = cache.current;
    // This camera + day's own: a request left over from another camera (the same clip names) must
    // not touch them.
    const pending = new Set();
    const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    let cancelled = false;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const filename = entry.target.getAttribute('data-filename');
        observer.unobserve(entry.target);
        if (!filename || thumbs.has(filename) || pending.has(filename)) return;
        pending.add(filename);
        fetchRelayBlobUrl(thumbUrl(station, index, filename), { signal: ctrl ? ctrl.signal : undefined })
          .then((u) => {
            if (cancelled || thumbs.has(filename)) {
              try { URL.revokeObjectURL(u); } catch (e) { /* noop */ }
              return;
            }
            thumbs.set(filename, u);
            setTick((n) => n + 1);
          })
          .catch(() => { /* no thumbnail yet */ })
          .finally(() => pending.delete(filename));
      });
    }, { root: null, rootMargin: '400px 0px', threshold: 0.01 });
    io.current = observer;
    return () => {
      cancelled = true;
      if (ctrl) ctrl.abort();
      observer.disconnect();
      io.current = null;
      thumbs.clear();
    };
  }, [station, index, date, active]);

  const observe = useCallback((el) => {
    if (el && io.current) io.current.observe(el);
  }, []);
  const thumbFor = useCallback((file) => cache.current.peek(file) || '', []);
  return { observe, thumbFor };
}

// One day's timeline per station, kept for the session. Today's is re-read
// after five minutes; past days never change. The Laser day takes ~3 s to
// build on the API, so the cache matters.
const timelineCache = new Map(); // key → { at, runs, promise }
const TODAY_TTL_MS = 5 * 60 * 1000;

export function loadStationRuns(station, date) {
  const key = `${station}|${date}`;
  const hit = timelineCache.get(key);
  const isToday = date === todayCentral();
  if (hit && (hit.promise || !isToday || Date.now() - hit.at < TODAY_TTL_MS)) {
    return hit.promise || Promise.resolve(hit.runs);
  }
  const promise = fetch(`${API_BASE}/stations/${encodeURIComponent(station)}/timeline/${date}`)
    .then((res) => {
      if (!res.ok) throw new Error(`timeline ${res.status}`);
      return res.json();
    })
    .then((data) => {
      const runs = mergeJobRuns(timelineBlocks(data, date));
      timelineCache.set(key, { at: Date.now(), runs });
      return runs;
    })
    .catch((e) => {
      timelineCache.delete(key);
      throw e;
    });
  timelineCache.set(key, { at: hit?.at || 0, runs: hit?.runs || [], promise });
  return promise;
}

/** { runs, loading, error } for each JobQ station in `stations` on `date`. */
export function useStationRuns(stations, date) {
  const [state, setState] = useState({});
  const key = (stations || []).filter(Boolean).join(',');
  useEffect(() => {
    let cancelled = false;
    const list = key ? key.split(',') : [];
    setState(Object.fromEntries(list.map((s) => [s, { runs: [], loading: true, error: false }])));
    list.forEach((s) => {
      loadStationRuns(s, date)
        .then((runs) => { if (!cancelled) setState((prev) => ({ ...prev, [s]: { runs, loading: false, error: false } })); })
        .catch(() => { if (!cancelled) setState((prev) => ({ ...prev, [s]: { runs: [], loading: false, error: true } })); });
    });
    return () => { cancelled = true; };
  }, [key, date]);
  return state;
}

// Exposed for tests.
export function _clearTimelineCache() {
  timelineCache.clear();
}
